/**
 * The onboarding catalog — every list the sequence renders, in one place.
 *
 * Source: the Claude Design prototype `Metanoia Onboarding v2.dc.html`
 * (project `ba89feb6-9533-43f4-80e9-3966e1780613`). The prototype has 14 steps;
 * this has 13, because its step 2 (account creation) is not part of the
 * sequence in the real app — onboarding runs AFTER sign-in and after the
 * early-access gate, so the account already exists by the time step 1 renders.
 *
 * ⚠️⚠️ EVERY `key` IN THIS FILE IS A STORAGE KEY. Reason keys, approach keys and
 * consent kinds are written to `user_onboarding` / `user_consents` and read back
 * for ever. Re-word a `label` freely; renaming a `key` silently orphans every
 * row that already carries the old one. (See the display-name-is-a-storage-key
 * lesson: a rename broke seven lookups and one was found thirteen days later.)
 */

/**
 * The seven act colours, cyan → orange.
 *
 * ⚠️ The three ANCHORS of this ramp (`#3CC2DD`, `#A739E2`, `#F58C56`) are also
 * hardcoded as a 3-stop gradient in `main.tsx`, `AvatarProgressRing` and
 * `HeaderAvatarReward`. They are not unified here on purpose: two of those
 * three paint the header avatar, which has its own history of specificity bugs,
 * and a shared constant is not worth reopening that. Unifying them is a
 * deliberate follow-up, not a side effect of building onboarding.
 */
export const SPECTRUM = [
  '#3CC2DD', '#6C85E0', '#8C5BE2', '#A739E2', '#BF4EBA', '#D7698F', '#F58C56',
] as const;

/** An evenly spaced sample of {@link SPECTRUM} for item `i` of `n`. */
export function spectrumAt(i: number, n: number): string {
  if (n <= 1 || i <= 0) return SPECTRUM[0];
  if (i >= n - 1) return SPECTRUM[SPECTRUM.length - 1];
  const pos = (i / (n - 1)) * (SPECTRUM.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(lo + 1, SPECTRUM.length - 1);
  const f = pos - lo;
  const channel = (j: number) => {
    const a = parseInt(SPECTRUM[lo].slice(1 + j * 2, 3 + j * 2), 16);
    const b = parseInt(SPECTRUM[hi].slice(1 + j * 2, 3 + j * 2), 16);
    return Math.round(a + (b - a) * f).toString(16).padStart(2, '0').toUpperCase();
  };
  // ⚠️ Uppercase to match SPECTRUM's own literals. Without this the endpoints
  // come back `#3CC2DD` and the interpolated stops `#6c85e0` — invisible in
  // CSS, but two spellings of one colour is exactly what makes a later
  // `colour === stop` comparison fail for no discoverable reason.
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

// ── Steps ───────────────────────────────────────────────────────────────────

export type OnboardingStepKey =
  | 'welcome'
  | 'motivation'
  | 'conditions'
  | 'roles'
  | 'journey'
  | 'about'
  | 'consents'
  | 'ready';

export interface OnboardingStepDef {
  key: OnboardingStepKey;
  /** Shown in the progress rail. */
  label: string;
  /** Small caps line above the title. */
  eyebrow: string;
  /** The screen's headline. ⭐ Rendered by the SHELL, not the body — on web it
   *  lives in the left pane beside the progress, which is what lets the right
   *  pane be nothing but the content and its controls. */
  title: string;
  /** True when the step only explains something and collects nothing. */
  teaching: boolean;
}

/**
 * ⚠️⚠️ THE LENGTH OF THIS ARRAY IS A DATABASE CONSTRAINT.
 * `user_onboarding.current_step` is `CHECK (current_step BETWEEN 1 AND 8)`
 * (migration 0255). Adding a step here without widening that check makes the
 * resume write fail — for the person on the last step, which is the worst
 * possible place to lose someone. A test pins the two together.
 *
 * ⭐ Eight, down from thirteen. The approach, rhythm and summary steps were cut
 * and name/birthday/identity merged into one, because the sequence was asking
 * for a plan before it had earned the right to — and every extra screen between
 * "I signed up" and "I am using it" is a place to leave.
 */
export const ONBOARDING_STEPS: readonly OnboardingStepDef[] = [
  { key: 'welcome', label: 'Welcome', eyebrow: 'A personal development initiative', title: 'Design your becoming.', teaching: true },
  { key: 'motivation', label: 'Motivation', eyebrow: 'Why you are here', title: 'What brought you here?', teaching: false },
  { key: 'conditions', label: 'Conditions', eyebrow: 'How change happens', title: 'Motivation starts change. Conditions sustain it.', teaching: true },
  { key: 'roles', label: 'Roles', eyebrow: 'Our role and yours', title: 'No system can live your life for you.', teaching: true },
  { key: 'journey', label: 'Journey', eyebrow: 'The seven stages', title: 'A cycle, not a ladder.', teaching: true },
  { key: 'about', label: 'About you', eyebrow: 'So we know who we are talking to', title: 'What should we call you?', teaching: false },
  { key: 'consents', label: 'Data & terms', eyebrow: 'Your data and boundaries', title: 'How Metanoia treats your information.', teaching: false },
  { key: 'ready', label: 'Ready', eyebrow: 'You are set up', title: 'Your journey has begun.', teaching: true },
] as const;

export const TOTAL_STEPS = ONBOARDING_STEPS.length;

export const stepIndexOf = (key: OnboardingStepKey): number =>
  ONBOARDING_STEPS.findIndex((s) => s.key === key) + 1;

// ── Step 2 · what brought you here ──────────────────────────────────────────

export interface ReasonDef {
  key: string;
  label: string;
  /** Sentence fragment for the closing screen: "You came here to …". */
  short: string;
}

export const REASONS: readonly ReasonDef[] = [
  { key: 'structure', label: 'More structure', short: 'build more structure' },
  { key: 'consistency', label: 'Consistency', short: 'become more consistent' },
  { key: 'self', label: 'Self-understanding', short: 'understand yourself better' },
  { key: 'health', label: 'Health & energy', short: 'improve your health and energy' },
  { key: 'direction', label: 'A clearer direction', short: 'clarify your direction' },
  { key: 'goal', label: 'An important goal', short: 'reach an important goal' },
  { key: 'momentum', label: 'Regaining momentum', short: 'regain momentum' },
  { key: 'multi', label: 'Growth across life', short: 'grow across several areas' },
  { key: 'explore', label: 'Just exploring', short: 'explore first' },
] as const;

// ── Step 3 · the five conditions ────────────────────────────────────────────

/**
 * The seven conditions. ⭐⭐ COMMITMENT AND CONSISTENCY ARE A PAIR, AND THE ORDER
 * IS THE ARGUMENT: commitment is what bridges fluctuating motivation into
 * repeated behaviour, so it comes first and consistency follows from it.
 *
 * Commitment = "I will keep doing this."  Consistency = "I actually keep doing
 * this." The first five explain whether someone CAN act and whether their
 * surroundings support acting; without these two the model says nothing about
 * staying with a change once motivation drops — which is the whole problem.
 *
 * ⚠️ Alternatives considered and rejected: Environment and Accountability sit
 * underneath Structure and Support, and Adaptability belongs to the Adapt stage.
 * Commitment is the genuinely missing psychological condition.
 */
export const CONDITIONS: readonly { name: string; desc: string }[] = [
  { name: 'Clarity', desc: 'Knowing what matters, and the next step.' },
  { name: 'Capacity', desc: 'Having the energy and time it requires.' },
  { name: 'Capability', desc: 'Having the skill to carry it out.' },
  { name: 'Structure', desc: 'Systems and routines that make action easier.' },
  { name: 'Support', desc: 'People and feedback that help you forward.' },
  { name: 'Commitment', desc: 'Choosing to continue when motivation changes.' },
  { name: 'Consistency', desc: 'Repeating the action often enough for change to take hold.' },
] as const;

// ── Step 4 · roles ──────────────────────────────────────────────────────────

/**
 * ⭐ The two sides are conceptually PARALLEL — five each, same grammar, no
 * possessive on the right. "Your choices" was cut because choice is the
 * umbrella over everything else on that side (direction, honesty, commitment
 * and consistency are all choices), and "Your pace" because pace is not
 * something the user provides — it is something the system owes them.
 *
 * ⭐⭐ COMMITMENT AND CONSISTENCY APPEAR ON BOTH SCREENS ON PURPOSE. They are two
 * of the seven conditions from the previous step, and this screen is where the
 * sequence says which of them the product cannot supply. That repetition is the
 * argument, not an accident — do not "de-duplicate" it.
 */
export const METANOIA_PROVIDES = [
  'Structure',
  'Knowledge',
  'Tools',
  'Community',
  'Feedback',
] as const;

/**
 * ⭐ Reads as a sequence of questions:
 *   Direction → Where am I going?      Honesty → Where am I really?
 *   Commitment → Am I choosing this?   Consistency → Am I continuing to act?
 *   Return → What happens when I don't?
 *
 * ⚠️ "The return after a setback" is kept in full rather than shortened to
 * resilience or persistence. Commitment does not mean never slipping and
 * consistency does not mean perfection; what matters is returning, and the
 * abstract nouns lose exactly that.
 */
export const YOU_PROVIDE = [
  'Direction',
  'Honesty',
  'Commitment',
  'Consistency',
  'The return after a setback',
] as const;

/**
 * ⚠️ NOT "The system is the how. The why is yours." That line conflicts with a
 * product whose Direction module exists to help people find their why — it
 * promises the opposite of a core feature.
 */
export const ROLES_FOOTER = 'The system supports the change. You are the one who lives it.';

/** Ties this screen back to the seven conditions rather than restating them. */
export const ROLES_BRIDGE =
  'Two of those conditions can only come from you: commitment and consistency.';

// ── Step 5 · the seven stages ───────────────────────────────────────────────

export const STAGES: readonly { numeral: string; name: string; desc: string; tools: string }[] = [
  { numeral: 'I', name: 'DISCOVER', desc: 'Understand your current reality — values, strengths, and patterns.', tools: 'Assessments · Status · Journaling' },
  { numeral: 'II', name: 'DEFINE', desc: 'Clarify who you want to become: purpose, vision, direction.', tools: 'Purpose · Vision · Missions' },
  { numeral: 'III', name: 'DESIGN', desc: 'Translate direction into goals, projects, routines, and habits.', tools: 'Goals · Quests · Projects · Abilities' },
  { numeral: 'IV', name: 'ACT', desc: 'Follow through with daily planning, focus, and intentional decisions.', tools: 'Scheduler · Tasks · Habits · Focus' },
  { numeral: 'V', name: 'REFLECT', desc: 'Record experiences, energy, progress, setbacks, and lessons.', tools: 'Reflection · Energy tracking · Reports' },
  { numeral: 'VI', name: 'ADAPT', desc: 'Review what works, find constraints, revise the approach.', tools: 'Reviews · Insights · Analytics' },
  { numeral: 'VII', name: 'BECOME', desc: 'Integrate growth into identity, lifestyle, and relationships.', tools: 'Levels · Skill trees · Milestones · Community' },
] as const;

// ── Step 6 · about you (name · birthday · identity) ─────────────────────────────────────────────────────

/**
 * ⚠️⚠️ TWO OPTIONS, AND THE STEP IS SKIPPABLE — those are separate facts and
 * both matter. The prototype offered four (adding self-describe and prefer-not-
 * to-say); the owner reduced it to two so the answer keeps feeding
 * `body_figure_gender`, which is a closed `'male' | 'female'` union because it
 * is interpolated into asset paths (`/bodyatlas/data/{gender}-front.json`) and
 * gates the Cycle tab.
 *
 * ⚠️⚠️ SKIPPING MUST LEAVE IT NULL, NOT 'male'. Null means *unanswered* and is
 * load-bearing: collapsing it to a default is exactly the bug that rendered
 * every user who never opened Settings as a male figure in the Body Atlas.
 */
export const IDENTITY_OPTIONS: readonly { key: 'male' | 'female'; label: string }[] = [
  { key: 'male', label: 'Male' },
  { key: 'female', label: 'Female' },
] as const;

// ── Step 7 · data and terms ────────────────────────────────────────────────

export const TRUST_POINTS: readonly { title: string; body: string }[] = [
  { title: 'You record', body: 'Reflections, goals, energy, emotions — you decide what is written.' },
  { title: 'Private', body: 'Nothing is visible to others unless you share it.' },
  { title: 'AI use', body: 'AI may surface patterns from your entries. You can turn this off.' },
  { title: 'Communities', body: 'Only your display name and shared milestones appear in guilds.' },
  { title: 'Your control', body: 'Export or delete everything, at any time.' },
] as const;

/**
 * Where the public documents live.
 *
 * ⚠️⚠️ ONE BASE URL, FOR THE SAME REASON THE EDGE FUNCTIONS HAVE ONE `APP_URL`.
 * ✅ Moved to `.com` 2026-08-31, once /terms, /privacy and /community-guidelines
 * had each been confirmed 200 on the new apex — this constant builds the links
 * a person must be able to READ before consenting, so it could not move on
 * faith. `.app` still resolves and redirects, so older builds keep working.
 * Override with `VITE_MARKETING_URL` at build time.
 *
 * ⭐ ALL THREE ARE PUBLISHED as of 2026-08-29. Until then only `/terms` existed,
 * and the consent step asked people to accept two documents they could not
 * read — which is not a consent at all. The mechanism that handled that (an
 * empty `path` renders the name plus "not published yet" rather than a dead
 * link) is deliberately KEPT: it is what makes adding a fourth document, or
 * temporarily unpublishing one, safe rather than silently broken.
 */
export const MARKETING_URL =
  (import.meta.env?.VITE_MARKETING_URL as string | undefined) ?? 'https://metanoiaos.com';

export interface LegalDocument {
  label: string;
  /** Path on the marketing site, or `''` when the document is not published. */
  path: string;
}

export const LEGAL_DOCUMENTS: Record<'terms' | 'privacy' | 'community', LegalDocument> = {
  terms: { label: 'Terms of Service', path: '/terms' },
  privacy: { label: 'Privacy Policy', path: '/privacy' },
  community: { label: 'Community Guidelines', path: '/community-guidelines' },
};

/** Absolute URL for a document, or null when it is not published. */
export const legalUrl = (doc: LegalDocument): string | null =>
  doc.path ? `${MARKETING_URL}${doc.path}` : null;

/**
 * ⚠️⚠️ `kind` MATCHES THE `user_consents.kind` CHECK CONSTRAINT (migration
 * 0255): 'legal' | 'community' | 'ai' | 'marketing'. A value not in that list
 * is rejected by the database, which is deliberate — a typo'd consent kind is a
 * record no reader will ever find, and that is indistinguishable from never
 * having asked the question.
 */
export type ConsentKind = 'legal' | 'community' | 'ai' | 'marketing';

export interface ConsentDef {
  kind: ConsentKind;
  label: string;
  /** One line on what agreeing actually means, in plain language. */
  detail: string;
  required: boolean;
  /** Default answer for the optional ones. Required ones always start false. */
  defaultGranted: boolean;
  /** Documents this row asks the reader to accept, so they can read them. */
  documents: readonly LegalDocument[];
}

export const CONSENTS: readonly ConsentDef[] = [
  {
    kind: 'legal',
    label: 'I accept the Terms of Service and Privacy Policy',
    detail: 'How the service works, and what happens to your data.',
    required: true,
    defaultGranted: false,
    documents: [LEGAL_DOCUMENTS.terms, LEGAL_DOCUMENTS.privacy],
  },
  {
    kind: 'community',
    label: 'I accept the Community Guidelines',
    detail: 'How people treat each other in guilds and shared spaces.',
    required: true,
    defaultGranted: false,
    documents: [LEGAL_DOCUMENTS.community],
  },
  {
    kind: 'ai',
    label: 'Metanoia may use AI to analyse my entries for insights',
    // ⚠️ Says what turning it off DOES, because it does something real: every
    // AI source is switched off, and the assistant stops reading your data.
    detail: 'Turning this off hides every source from the assistant. Settings → Assistant.',
    required: false,
    defaultGranted: true,
    documents: [],
  },
  {
    kind: 'marketing',
    label: 'Send me product updates and growth resources',
    detail: 'Occasional email. Unsubscribe whenever you like.',
    required: false,
    defaultGranted: false,
    documents: [],
  },
] as const;

/** Shown under the consent list — none of this is a one-time, final decision. */
export const CONSENT_REVERSIBLE_NOTE =
  'You can change any of these later in Settings, including withdrawing consent.';

/**
 * The version stamped onto every consent row written by this build.
 *
 * ⚠️ Bump this whenever the Terms, Privacy Policy or Community Guidelines
 * change materially. The ledger's whole value is answering "what did they agree
 * to, and when" — a consent row pointing at an unversioned document answers
 * only half of that.
 */
export const CONSENT_DOCUMENT_VERSION = '2026-08';
